import { useEffect, useState, useCallback } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import * as routeTags from 'hb-react/publicoffice/constants/routeTags';
import handleFetchRequest from 'hb-react/publicoffice/utils/handleFetchRequest';
import { preLoadLaunchDarklyProviderConfig } from 'hb-react/shared/utils/launchDarkly';

import {
  type UseNetworkConfigurationProps,
  type URLObject,
  type FetchResponseProps,
  type NetworkConfigurationProps,
} from './types';

export default ({
  colorNeeded = true,
  networkConfigurationOptions,
  networkRouteTags,
  enabled = true,
  styleNeeded = true,
  themingNeeded = true,
}: UseNetworkConfigurationProps) => {
  // Skip during tests so we don't have to add the feature_flag_settings_path handler everywhere.
  if (
    window.__HB_ENV__.LAUNCHDARKLY_CLIENT_SIDE_ID &&
    process.env.NODE_ENV !== 'test'
  ) {
    // Performance Optimization: Launchdarkly flags are required down the road. Load them preemptively.
    preLoadLaunchDarklyProviderConfig();
  }

  const [networkConfiguration, setNetworkConfiguration] =
    useState<NetworkConfigurationProps>();

  const [isError, setIsError] = useState(false);

  const getURLs = useCallback(() => {
    const URLs: Array<URLObject> = [];

    if (colorNeeded) {
      URLs.push({
        objectKey: 'colors',
        path: Routes.colors_session_info_index_path(),
      });
    }
    if (styleNeeded) {
      URLs.push({
        objectKey: 'styles',
        path: Routes.styles_session_info_index_path(),
      });
    }
    if (networkConfigurationOptions) {
      URLs.push({
        objectKey: 'networkConfigData',
        path: Routes.network_configuration_session_info_index_path(),
        params: { props: networkConfigurationOptions },
      });
    }
    if (themingNeeded) {
      URLs.push({
        objectKey: 'theming',
        path: Routes.theming_session_info_index_path(),
      });
    }

    networkRouteTags?.forEach((networkRouteTag) => {
      switch (networkRouteTag.routeTag) {
        case routeTags.ROUTE_TAG_FEATURES:
          URLs.push({
            objectKey: 'featuresData',
            path: Routes.features_session_info_index_path(),
            params: networkRouteTag.params,
          });
          break;
        case routeTags.ROUTE_TAG_APP_PAGES:
          URLs.push({
            objectKey: 'appPagesData',
            path: Routes.app_pages_session_info_index_path(),
            params: networkRouteTag.params,
          });
          break;
        case routeTags.ROUTE_TAG_CURRENT_NETWORK:
          URLs.push({
            objectKey: 'currentNetwork',
            path: Routes.current_network_session_info_index_path(),
          });
          break;
        case routeTags.ROUTE_TAG_PAGE_CUSTOMIZABLES:
          URLs.push({
            objectKey: 'pageCustomizables',
            path: Routes.page_customizables_session_info_index_path(),
          });
          break;
        case routeTags.ROUTE_TAG_LOGIN_SETTINGS:
          URLs.push({
            objectKey: 'loginSettings',
            path: Routes.login_settings_session_info_index_path(),
          });
          break;
        default:
      }
    });

    return URLs;
  }, [
    colorNeeded,
    styleNeeded,
    networkConfigurationOptions,
    networkRouteTags,
    themingNeeded,
  ]);

  const handleNetworkConfiguration = useCallback(async () => {
    try {
      const URLs = getURLs();

      if (URLs.length > 0) {
        const responses: Array<FetchResponseProps> = await handleFetchRequest({
          URLs,
        });

        const config = Object.fromEntries(
          responses.map((response) => [response.id, response.data]),
        );

        setIsError(false);
        setNetworkConfiguration(config);
      }
    } catch {
      toast.error(__('Sorry, an error occurred'));
      setIsError(true);
    }
  }, [getURLs]);

  useEffect(() => {
    // Do not clean this condition until all the waves of po pages as spa are done
    if (enabled) {
      handleNetworkConfiguration();
    }
  }, [enabled, handleNetworkConfiguration]);

  return { networkConfiguration, isError };
};
